import { Button, FlexBox, FloatingLabel, FormError, FormField, FormInputGroup, InputWithFloatingLabel, StandardForm, TextArea, TextInput } from '@vp/swan';
import React, { useContext, useEffect, useState } from 'react';
import { IStartChatData } from '../../interfaces/api-interfaces-miaw';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { CAContext } from '../../contexts/CAContext';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { validateEmail, validateName, validatechatbotContactReason, validateOrderNumber } from '../../utils/validation-utils';
import useSummaryFlowLogic from './useSummaryFlowLogic';

export interface IPrechatFormProps {
  onSubmit?: (data: IStartChatData) => void;
  correctSummary?: boolean;
  typeOfFLow?: 'moneyBack' | 'transferToAgent' | 'nonLoggedInPreChatWithOrderNumber' | 'globalException';
}
interface IFormData {
  firstName?: string | undefined;
  lastName?: string | undefined;
  email?: string | undefined;
  chatbotContactReason?: string | undefined;
  isAPICalled?: boolean;
  orderNumber?: string | undefined;
  isSummaryGenerated?: boolean;
}

type ValidatableField = 'firstName' | 'lastName' | 'email' | 'chatbotContactReason' | 'orderNumber';

const PrechatForm: React.FC<IPrechatFormProps> = ({ onSubmit, correctSummary, typeOfFLow }) => {
  const {
    state: { sessionId, orderNumber: CaOrderNumber },
    setState: setChatbotState,
  } = useContext(ChatbotContext);

  const {
    state: { startMiawChatSession },
  } = useContext(MiawChatContext);

  const {
    chatState: { userInfo },
  } = useContext(CAContext);

  const [formData, setFormData] = useState<IFormData>({
    firstName: '',
    lastName: '',
    email: '',
    chatbotContactReason: '',
    isAPICalled: false,
    orderNumber: '',
  });

  const [errors, setErrors] = useState<Record<ValidatableField, string>>({
    firstName: '',
    lastName: '',
    email: '',
    chatbotContactReason: '',
    orderNumber: '',
  });

  const [touched, setTouched] = useState<Record<ValidatableField, boolean>>({
    firstName: false,
    lastName: false,
    email: false,
    chatbotContactReason: false,
    orderNumber: false,
  });

  const [autoFilled, setAutoFilled] = useState(false);

  const { handleSummaryGenerated } = useSummaryFlowLogic({ formData, setAutoFilled, setFormData, correctSummary });

  const handleBlur = (field: ValidatableField) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    let error = '';
    switch (field) {
      case 'email':
        error = validateEmail(userInfo?.userData?.email || formData?.email || '');
        break;
      case 'chatbotContactReason':
        error = validatechatbotContactReason(formData?.chatbotContactReason || '');
        break;
      case 'orderNumber':
        error = validateOrderNumber(formData?.orderNumber || '');
        break;
      default:
        error = validateName(formData[field] || '', field === 'firstName' ? 'First name' : 'Last name');
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<ValidatableField, string> = {
      firstName: userInfo?.isLoggedIn && userInfo?.userData ? '' : validateName(formData.firstName || '', 'First name'),
      lastName: userInfo?.isLoggedIn && userInfo?.userData ? '' : validateName(formData.lastName || '', 'Last name'),
      email: userInfo?.isLoggedIn && userInfo?.userData ? '' : validateEmail(userInfo?.userData?.email || formData.email || ''),
      chatbotContactReason: validatechatbotContactReason(formData.chatbotContactReason || ''),
      orderNumber: typeOfFLow === 'nonLoggedInPreChatWithOrderNumber' ? validateOrderNumber(formData.orderNumber || '') : '',
    };

    setErrors(newErrors);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      chatbotContactReason: true,
      orderNumber: true,
    });

    return Object.values(newErrors).every((e) => !e);
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const userData = userInfo?.userData ? userInfo.userData : null;

    if (formData.orderNumber && !CaOrderNumber) {
      setChatbotState((prev) => ({
        ...prev,
        orderNumber: formData.orderNumber || '',
      }));
    }

    const payloadToSend: IStartChatData = {
      email: userData?.email || formData.email || '',
      firstName: userData?.firstName || formData.firstName || formData.email || formData.email || '',
      lastName: userData?.lastName || formData.lastName || userData?.firstName || userData?.email || '',
      chatbotIdentifier: sessionId || '',
      chatbotContactReason: formData.chatbotContactReason || '',
      orderNumber: CaOrderNumber || userData?.orderNumber?.trim()?.toUpperCase() || formData?.orderNumber?.trim()?.toUpperCase() || '',
      isSummaryGenerated: autoFilled,
      typeOfFLow: typeOfFLow || '',
    };

    onSubmit?.(payloadToSend);

    if (correctSummary) {
      return;
    }

    if (autoFilled) {
      handleSummaryGenerated();
    } else {
      startMiawChatSession?.(payloadToSend);
    }
  };

  useEffect(() => {
    if (userInfo?.isLoggedIn && userInfo.userData && autoFilled && formData.isAPICalled) {
      handleSubmit();
    }
  }, [formData.isAPICalled]);

  return formData.isAPICalled ? (
    <StandardForm compactMode marginY="between-actions">
      {!userInfo?.isLoggedIn && !correctSummary && (
        <>
          <FormField mb="5">
            <FormInputGroup>
              <TextInput
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                onBlur={() => handleBlur('firstName')}
                placeholder="First name"
              />
              {touched.firstName && errors.firstName && <FormError>{errors.firstName}</FormError>}
            </FormInputGroup>
          </FormField>
          <FormField mb="5">
            <FormInputGroup>
              <TextInput
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                onBlur={() => handleBlur('lastName')}
                placeholder="Last name"
              />
              {touched.lastName && errors.lastName && <FormError>{errors.lastName}</FormError>}
            </FormInputGroup>
          </FormField>
          <FormField>
            <FormInputGroup>
              <TextInput
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                onBlur={() => handleBlur('email')}
                placeholder="Email linked to your VistaPrint account"
                hidden={userInfo?.userData?.email ? true : false}
              />
              {touched.email && errors.email && <FormError>{errors.email}</FormError>}
            </FormInputGroup>
          </FormField>
          {typeOfFLow === 'nonLoggedInPreChatWithOrderNumber' && (
            <FormField>
              <FormInputGroup>
                <TextInput
                  value={formData.orderNumber}
                  onChange={(e) => setFormData({ ...formData, orderNumber: e.target.value })}
                  onBlur={() => handleBlur('orderNumber')}
                  placeholder="Order number"
                />
                {touched.orderNumber && errors.orderNumber && <FormError>{errors.orderNumber}</FormError>}
              </FormInputGroup>
            </FormField>
          )}
        </>
      )}
      {!autoFilled && !correctSummary && (
        <>
          <FormField>
            <FormInputGroup>
              <TextArea
                fullWidth
                value={formData.chatbotContactReason}
                onChange={(e) => setFormData({ ...formData, chatbotContactReason: e.target.value })}
                onBlur={() => handleBlur('chatbotContactReason')}
                placeholder="Contact reason"
              />
              {touched.chatbotContactReason && errors.chatbotContactReason && <FormError>{errors.chatbotContactReason}</FormError>}
            </FormInputGroup>
          </FormField>
        </>
      )}
      {correctSummary && (
        <FormField>
          <FormInputGroup>
            <InputWithFloatingLabel>
              <FloatingLabel>Contact reason</FloatingLabel>
              <TextArea
                pt={7}
                fullWidth
                value={formData.chatbotContactReason}
                onChange={(e) => setFormData({ ...formData, chatbotContactReason: e.target.value })}
                onBlur={() => handleBlur('chatbotContactReason')}
                placeholder="Contact reason"
                rows={5}
                resize={'both'}
              />
            </InputWithFloatingLabel>

            {touched.chatbotContactReason && errors.chatbotContactReason && <FormError>{errors.chatbotContactReason}</FormError>}
          </FormInputGroup>
        </FormField>
      )}
      <FlexBox justifyContent="space-around">
        <Button onClick={handleSubmit} skin="primary">
          Confirm
        </Button>
      </FlexBox>
    </StandardForm>
  ) : null;
};

export default PrechatForm;
